import { ToggleVisibilityButton } from "components/buttons/linkButtons/toggleVisibilityButton/ToggleVisibilityButton";
import { TextTruncate } from "../../../../components";
import { ContentItemMenu } from "../../../../components/buttons/contentItemMenu/ContentItemMenu";
import {
  EditLinkButton,
  DuplicateLinkButton,
  DeleteLinkButton,
  ViewContentLinkButton,
} from "../../../../components/buttons/linkButtons";
import { PublishStatus } from "components/common/PublishStatus";
import { Tooltip } from "../../../../components/common/tooltip";
import { Title as TitleComponent } from "../../../../components/listViewTemplates";
import DeletionRestrictedTooltip from "../../../../components/tooltips/deletionRestrictedTooltip/DeletionRestrictedTooltip";
import { ContentTypesEnum, PublishedStatusTypes, RolePermissions } from "../../../../enums";
import SortOptions from "../../../../enums/SortOptions";
import { type ColumnOption } from "../../../../interfaces/columnOptions";
import type SortingOptions from "../../../../interfaces/SortingOptions";
import dateTimeUtils from "../../../../utils/dateTimeUtils";
import { type EventOverview } from "../types/state";
import { getContentLinkToEupFromPreview } from "utils/eupUtils";
import cn from "classnames";
import { HiddenContent } from "components/HiddenContent/HiddenContent";

export enum Columns {
  Title = "Title",
  Publisher = "Publisher",
  Status = "Status",
  Modified = "Modified",
  Added = "Added",
}

export const sortingOptions: SortingOptions[] = [
  {
    text: "Most Recent",
    value: SortOptions.Id,
    default: true,
  },
  {
    text: "Last Modified",
    value: SortOptions.ModifiedDateDesc,
  },
  {
    text: "Sorted A-Z",
    value: SortOptions.Title,
  },
];

export interface GetColumnOptionsParams {
  readonly?: boolean;
  deleteContentWithDependenciesFeatureEnabled: boolean;
  visibility: boolean;

  buttonHandlers: {
    handleEditClick: (id: number, isDraft: boolean) => () => void;
    handleDuplicateClick: (selectedIds: number[]) => () => void;
    handleDeleteClick: (selectedIds: number[], flowsCount?: number, packsCount?: number) => () => void;
    onToggleVisibility: (id: number[], hidden: boolean) => () => void;
  };
}

export const getColumnOptions = (params: GetColumnOptionsParams): ColumnOption<EventOverview>[] => [
  {
    name: Columns.Title,
    width: 4,
    isSortable: true,
    render: (event) => (
      <HiddenContent variant="list" isHidden={!event.visibility} showIcon>
        <TitleComponent
          url={event.id.toString()}
          title={event.title}
          clamp={2}
          className={cn("normal-color", { "hidden-content": !event.visibility })}
        />
      </HiddenContent>
    ),
  },
  {
    name: Columns.Publisher,
    width: 3,
    isSortable: false,
    render: (event) => (
      <Tooltip
        target={
          <TextTruncate>
            <HiddenContent variant="list" isHidden={!event.visibility}>
              {event.publisher}
            </HiddenContent>
          </TextTruncate>
        }
        className={cn("normal-color", { "hidden-content": !event.visibility })}
        content={event.publisher}
      />
    ),
  },
  {
    name: Columns.Status,
    width: 3,
    isSortable: true,
    render: (event) => (
      <HiddenContent variant="list" isHidden={!event.visibility}>
        <PublishStatus
          publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(!event.isDraft, event.hasBeenPublished)}
        />
      </HiddenContent>
    ),
  },
  {
    name: Columns.Modified,
    width: 2,
    isSortable: true,
    render: (event) => (
      <HiddenContent variant="list" isHidden={!event.visibility}>
        {dateTimeUtils.formatDate(event.dateModified)}
      </HiddenContent>
    ),
  },
  {
    name: Columns.Added,
    width: 2,
    isSortable: true,
    render: (event) => (
      <HiddenContent variant="list" isHidden={!event.visibility}>
        {dateTimeUtils.formatDate(event.dateCreated)}
      </HiddenContent>
    ),
  },
  {
    name: "",
    width: 1,
    className: "align-right",
    isSortable: false,
    render: (item, isChecked) => {
      const { flowsCount = 0, packsCount = 0, assignmentsCount = 0 } = item.dependencies || {};
      const dependencies = { flows: flowsCount, packs: packsCount, "users or groups": assignmentsCount };
      const entityType = "event";
      return (
        <ContentItemMenu
          circle
          outlinedEllipsis
          isChecked={isChecked}
          item={item}
          readonly={params.readonly}
          entityType={entityType}
          dependencies={dependencies}
        >
          <ViewContentLinkButton
            href={getContentLinkToEupFromPreview[ContentTypesEnum.Events](item.id)}
            disabled={!item.hasBeenPublished}
          />
          {params.visibility && item.hasBeenPublished && (
            <ToggleVisibilityButton
              onClick={params.buttonHandlers.onToggleVisibility([item.id], !item.visibility)}
              hidden={!item.visibility}
              requiredPermissions={[RolePermissions.EventsCreate]}
            />
          )}
          {!item.isPurchased && (
            <EditLinkButton
              isDisabled={item.isPurchased}
              onClick={params.buttonHandlers.handleEditClick(item.id, item.isDraft)}
            />
          )}
          {!item.isPurchased && (
            <DuplicateLinkButton
              isDisabled={item.isPurchased}
              onClick={params.buttonHandlers.handleDuplicateClick([item.id])}
            />
          )}
          {!item.isPurchased && (
            <DeleteLinkButton
              isDisabled={!params.deleteContentWithDependenciesFeatureEnabled && !item.canBeDeleted}
              after={
                !params.deleteContentWithDependenciesFeatureEnabled &&
                !item.isPurchased &&
                hasDeletionDependencies(dependencies) && (
                  <DeletionRestrictedTooltip dependencies={dependencies} entityType={entityType} />
                )
              }
              onClick={params.buttonHandlers.handleDeleteClick([item.id], flowsCount, packsCount)}
            />
          )}
        </ContentItemMenu>
      );
    },
  },
];

export const hasDeletionDependencies = (dependencies: { [key: string]: number }) => {
  return !!Object.values(dependencies).reduce((sum, value) => sum + value);
};
